import type { SandboxErrorKind } from './types';

/** 表示文言（sandboxd-client.md §5）。rejected は serverMessage を使う。 */
export const SANDBOX_ERROR_MESSAGES: Record<
  Exclude<SandboxErrorKind, 'rejected' | 'server'>,
  string
> = {
  'local-busy': '実行中です。',
  busy: '混み合っています。少し待ってからもう一度実行してください。',
  timeout: '実行サーバーの応答が時間内に返りませんでした。',
  network: '実行サーバーに接続できません。',
  protocol: '実行結果を読み取れませんでした。',
};

/** 再実行ボタンを出すか（§5）。 */
export function isRetryable(kind: SandboxErrorKind): boolean {
  return kind !== 'local-busy' && kind !== 'rejected';
}

function messageFor(
  kind: SandboxErrorKind,
  status: number | null,
  serverMessage: string | null,
): string {
  if (kind === 'rejected') return serverMessage ?? 'リクエストが受け付けられませんでした。';
  if (kind === 'server') return `実行サーバーでエラーが発生しました（HTTP ${status ?? '?'}）。`;
  return SANDBOX_ERROR_MESSAGES[kind];
}

/**
 * 判定を出せなかったときのエラー（sandboxd-client.md §4.6）。
 * message は §5 の表示文言。学習者のコードや出力は入れない（observability.md §5）。
 */
export class SandboxError extends Error {
  readonly kind: SandboxErrorKind;
  readonly status: number | null;
  readonly serverMessage: string | null;

  constructor(
    kind: SandboxErrorKind,
    status: number | null = null,
    serverMessage: string | null = null,
  ) {
    super(messageFor(kind, status, serverMessage));
    this.name = 'SandboxError';
    this.kind = kind;
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

/** 応答本文が契約に合わない（judge.md §3.4）。UI では protocol 分類として扱う。 */
export class ProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProtocolError';
  }
}

/** 呼び出し側の中止（§4.2）。UI にエラーを出さない。 */
export function createAbortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError');
}

export function isAbortError(e: unknown): boolean {
  return (e instanceof DOMException || e instanceof Error) && e.name === 'AbortError';
}

/** 再試行の待機中に出す文言（§4.4）。 */
export function retryNotice(attempt: 1 | 2): string {
  return `混み合っています。再試行します（${attempt}/2）`;
}
